import React, { useEffect, useState } from 'react';
import { ExclamationTriangleIcon, XMarkIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { forceRelogin, checkSessionValid } from '../hooks/useAuth';

// Global banner shown when an API request fails at the NETWORK level (a fetch
// rejection that surfaces as "Failed to fetch") — this bypasses the normal HTTP
// 401 auto-relogin in client.js, so without this the user just sees a raw
// "Failed to fetch" with no guidance. client.js dispatches the `api:connection-error`
// window event after its one silent-retry fails.
//
// On that event we run checkSessionValid() — a redirect-free MSAL probe — to
// decide WHY the request failed and tailor the guidance:
//   • 'expired'  → the sign-in is genuinely gone → lead with "Sign in again".
//   • 'valid'    → the session is fine, the connection just dropped → lead with
//                  "Reload"/retry and DON'T nag about signing in.
//   • 'unknown'/'checking' → can't tell → offer Reload primarily, sign-in as a
//                  secondary fallback.
// (In practice getApiToken() already redirects on a truly-expired session before
// the fetch even runs, so 'valid' is by far the common case — which is exactly
// why the old blanket "your session may have expired" copy was almost always wrong.)
export default function ConnectionErrorBanner() {
  const [show, setShow] = useState(false);
  const [sessionStatus, setSessionStatus] = useState('checking'); // checking | valid | expired | unknown

  useEffect(() => {
    let active = true;
    const onErr = () => {
      setShow(true);
      setSessionStatus('checking');
      checkSessionValid()
        .then((s) => { if (active) setSessionStatus(s); })
        .catch(() => { if (active) setSessionStatus('unknown'); });
    };
    window.addEventListener('api:connection-error', onErr);
    return () => { active = false; window.removeEventListener('api:connection-error', onErr); };
  }, []);

  if (!show) return null;

  const expired = sessionStatus === 'expired';
  const heading = expired ? 'Your session has expired' : "Couldn't reach the server";
  let body;
  if (expired) {
    body = 'You have been signed out. Sign in again to pick up where you left off — your work is saved.';
  } else if (sessionStatus === 'checking') {
    body = 'The connection dropped. Checking whether you’re still signed in…';
  } else {
    body = 'The connection dropped, but you’re still signed in — this is usually temporary. Wait a moment and try again, or reload the page.';
  }

  return (
    <div className="fixed top-0 inset-x-0 z-[100] flex justify-center px-4 pt-3 pointer-events-none">
      <div className="pointer-events-auto max-w-2xl w-full rounded-lg border border-amber-600/70 bg-amber-950/95 shadow-lg backdrop-blur px-4 py-3 flex items-start gap-3">
        <ExclamationTriangleIcon className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1 text-sm text-amber-100">
          <div className="font-semibold text-amber-200">{heading}</div>
          <p className="mt-0.5 text-amber-100/90">{body}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {expired ? (
              <>
                <button onClick={() => forceRelogin()} className="btn-primary text-xs px-3 py-1.5">
                  Sign in again
                </button>
                <button
                  onClick={() => window.location.reload()}
                  className="btn-secondary text-xs px-3 py-1.5 flex items-center gap-1"
                >
                  <ArrowPathIcon className="h-3.5 w-3.5" /> Reload
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => window.location.reload()}
                  className="btn-primary text-xs px-3 py-1.5 flex items-center gap-1"
                >
                  <ArrowPathIcon className="h-3.5 w-3.5" /> Reload
                </button>
                {sessionStatus === 'unknown' && (
                  <button onClick={() => forceRelogin()} className="btn-secondary text-xs px-3 py-1.5">
                    Sign in again
                  </button>
                )}
              </>
            )}
          </div>
        </div>
        <button
          onClick={() => setShow(false)}
          className="text-amber-400 hover:text-amber-200 flex-shrink-0"
          aria-label="Dismiss"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
